import React, { useEffect, useState } from 'react';
import { Box, VStack, Heading, Text, FormControl, FormLabel, Input, Button, Checkbox, Alert, AlertIcon, Spinner, useToast } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabaseClient';

// Lands here after the phleb clicks the "set your password" email link.
// Supabase puts an access_token in the URL hash and creates a session for them,
// at which point auth.updateUser({password}) is allowed.

export default function SetPassword() {
  const navigate = useNavigate();
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [authedEmail, setAuthedEmail] = useState(null);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Supabase v2 auto-consumes the access_token from the URL hash on import.
      const { data: { session } } = await supabase.auth.getSession();
      if (cancelled) return;
      if (!session?.user) {
        setError('This reset link has expired or is invalid. Ask your admin to send a new one.');
      } else {
        setAuthedEmail(session.user.email || null);
      }
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  const submit = async () => {
    if (password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    if (password !== confirm) { setError("Passwords don't match."); return; }
    setSubmitting(true);
    setError(null);
    try {
      const { error: updErr } = await supabase.auth.updateUser({ password });
      if (updErr) throw updErr;

      // Stamp password_set_at so admin sees the green badge.
      const { data: { user } } = await supabase.auth.getUser();
      if (user?.id) {
        await supabase
          .from('phlebotomist_profiles')
          .update({ password_set_at: new Date().toISOString() })
          .eq('id', user.id);
      }

      toast({ title: 'Password set!', status: 'success', duration: 3000 });
      // Sign them out so they hit the login screen with their fresh credentials.
      await supabase.auth.signOut();
      navigate('/phlebotomist/login');
    } catch (e) {
      setError(e.message || 'Could not set password');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <Box display="flex" h="60vh" alignItems="center" justifyContent="center"><Spinner /></Box>;
  }

  return (
    <Box maxW="480px" mx="auto" mt={10} p={6} bg="white" borderRadius="xl" boxShadow="md">
      <VStack align="stretch" spacing={4}>
        <Heading size="md">Set your password</Heading>
        {authedEmail && (
          <Text fontSize="sm" color="gray.600">
            For <b>{authedEmail}</b>. After saving you'll sign in with this password.
          </Text>
        )}

        {error && <Alert status="error" fontSize="sm"><AlertIcon />{error}</Alert>}

        <FormControl isRequired isDisabled={!authedEmail}>
          <FormLabel fontSize="sm">New password</FormLabel>
          <Input type={show ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" />
        </FormControl>
        <FormControl isRequired isDisabled={!authedEmail}>
          <FormLabel fontSize="sm">Confirm password</FormLabel>
          <Input type={show ? 'text' : 'password'} value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="new-password" />
        </FormControl>
        <Checkbox isChecked={show} onChange={(e) => setShow(e.target.checked)} size="sm">Show password</Checkbox>

        <Button
          colorScheme="blue"
          isDisabled={!authedEmail || password.length < 8 || password !== confirm || submitting}
          isLoading={submitting}
          onClick={submit}
        >
          Save password
        </Button>
      </VStack>
    </Box>
  );
}
